import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import * as L from 'leaflet';
import * as Turf from '@turf/turf';
import { Position } from '@turf/turf';
import { Coordinates } from '../../model/map.model';
import { SearchMapDialogComponent } from '../search-map-dialog/search-map-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class MapOperationsService {
  private dialogService = inject(DialogService);
  private translateService = inject(TranslateService);

  private currentMarkerSignal: WritableSignal<L.Marker | null> = signal(null);
  private mapInstanceSignal: WritableSignal<L.Map | null> = signal(null);
  private resizeMapSignal: WritableSignal<number> = signal(0);

  readonly currentMarker = this.currentMarkerSignal.asReadonly();
  readonly mapInstance = this.mapInstanceSignal.asReadonly();
  readonly resizeMap = this.resizeMapSignal.asReadonly();

  private markerIcon = {
    icon: L.icon({
      iconSize: [25, 41],
      iconAnchor: [10, 41],
      popupAnchor: [2, -40],
      iconUrl: 'assets/img/marker-icon.png',
      shadowUrl: 'assets/img/marker-shadow.png'
    })
  };

  setMapInstance(map: L.Map): void {
    this.mapInstanceSignal.set(map);
    this.setupCoordinatesDisplay(map);
  }

  zoomIn(): void {
    const map = this.mapInstanceSignal();
    if (map) {
      map.zoomIn();
    }
  }

  zoomOut(): void {
    const map = this.mapInstanceSignal();
    if (map) {
      map.zoomOut();
    }
  }

  addMarker(latlng: L.LatLng): void {
    const map = this.mapInstanceSignal();
    if (!map) return;

    const currentMarker = this.currentMarkerSignal();
    if (currentMarker) {
      map.removeLayer(currentMarker);
    }

    const marker = L.marker(latlng, this.markerIcon)
      .bindTooltip(`${latlng.lat.toFixed(4)} ${latlng.lng.toFixed(4)}`, {
        permanent: true,
        direction: 'right'
      })
      .on('click', () => {
        const current = this.currentMarkerSignal();
        if (current && map) {
          map.removeLayer(current);
          this.currentMarkerSignal.set(null);
        }
      });

    marker.addTo(map);
    this.currentMarkerSignal.set(marker);
  }

  addSearchMarker(coordinates: Coordinates): void {
    const map = this.mapInstanceSignal();
    if (!map) return;

    const currentMarker = this.currentMarkerSignal();
    if (currentMarker) {
      map.removeLayer(currentMarker);
    }

    const latLng = new L.LatLng(coordinates.latitude, coordinates.longitude);
    const marker = L.marker(latLng, this.markerIcon).bindTooltip(`${latLng.lat.toFixed(4)} ${latLng.lng.toFixed(4)}`, {
      permanent: true,
      direction: 'right'
    });

    marker.addTo(map);
    if (map.getContainer()) {
      try {
        map.setView(latLng, 10);
      } catch (error) {
        console.warn('Failed to set map view for search marker:', error);
      }
    }
    this.currentMarkerSignal.set(marker);
  }

  openSearchDialog(): Promise<Coordinates | null> {
    return new Promise((resolve) => {
      const ref: DynamicDialogRef = this.dialogService.open(SearchMapDialogComponent, {
        header: this.translateService.instant('dialogs.searchMap.header'),
        contentStyle: { overflow: 'auto' },
        width: '40rem',
        baseZIndex: 10000,
        closable: true
      });

      ref.onClose.subscribe((data: Coordinates) => {
        resolve(data || null);
      });
    });
  }

  isPointInGeometry(latLng: L.LatLng, geometry: GeoJSON.Geometry): boolean {
    if (geometry.type === 'Polygon') {
      try {
        const clickPoint = Turf.point([latLng.lng, latLng.lat]);
        const coordinates: Position[][] = geometry.coordinates as Position[][];
        const polygon = Turf.polygon(coordinates);
        return Turf.booleanPointInPolygon(clickPoint, polygon);
      } catch (error) {
        console.error('Error creating Polygon in Turf.js', error);
      }
    } else if (geometry.type === 'MultiPolygon') {
      try {
        const clickPoint = Turf.point([latLng.lng, latLng.lat]);
        const coordinates: Position[][][] = geometry.coordinates as Position[][][];

        for (const coords of coordinates) {
          const polygon = Turf.polygon(coords);
          if (Turf.booleanPointInPolygon(clickPoint, polygon)) {
            return true;
          }
        }
      } catch (error) {
        console.error('Error creating MultiPolygon in Turf.js', error);
      }
    }
    return false;
  }

  isPointInLayer(latLng: L.LatLng, layer: L.GeoJSON): boolean {
    if (layer.feature) {
      const feature = layer.feature;
      if (feature.type === 'Feature' && feature.geometry) {
        return this.isPointInGeometry(latLng, feature.geometry);
      }
    }
    return false;
  }

  setResizeMap(): void {
    this.resizeMapSignal.update((value) => value + 1);
  }

  private setupCoordinatesDisplay(map: L.Map): void {
    const Coordinates = L.Control.extend({
      onAdd: (mapCoordinates: L.Map) => {
        const container = L.DomUtil.create('div', 'coordinates-container');
        container.setAttribute('id', 'coordinatesContainer');
        mapCoordinates.addEventListener('mousemove', (e: L.LeafletMouseEvent) => {
          container.innerHTML = `
            <div class='latlngInfo'>
            <strong>${e.latlng.lat.toFixed(4)} ${e.latlng.lng.toFixed(4)}</strong>
            </div>
            `;
        });
        return container;
      }
    });
    map.addControl(new Coordinates({ position: 'bottomright' }));
  }
}
